import { Component, OnInit, AfterContentInit, OnChanges, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';

import { HelperService } from 'app/services';
import { AllowDecimalMask } from 'app/services/sharedInterfaces';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
})
export class InputComponent implements OnInit, OnChanges, AfterContentInit {
  @Input() value: string = '';
  @Input() placeholder: string = '';
  @Input() label: string = this.placeholder;
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() validationId: string = '';
  @Input() type: string = 'text'; // [text, number]
  @Input() patterns: string = '';
  @Input() errorMessage: string = '';
  @Input() warningMessage: string = '';
  @Input() isInvalid: boolean = false;
  @Input() isWarning: boolean = false;
  @Input() disabled: boolean = false;
  @Input() floatingLabel: boolean = false;
  @Input() styleClass: string = '';
  @Input() styleClassContainer: string = '';
  @Input() styleClassLabel: string = '';
  @Input() styleClassError: string = '';
  @Input() styleClassInput: string = '';
  @Input() styleClassButton: string = '';
  @Input() labelFontSize: string = '';
  @Input() maxLength: number = 500;
  @Input() thousandSeparator: string = '';
  @Input() prefix: string = '';
  @Input() suffix: string = '';
  @Input() cancelHover: string = '';
  @Input() theme: string = 'primary'; // [light, primary, secondary, border]
  @Input() textAlign: string = 'left'; // [left, right]
  @Input() hasHoverEffect: boolean = false;
  @Input() hasBgOnActive: boolean = false;
  @Input() hasHoverBorderBottom: boolean = true;
  @Input() hasBorderBottomOnActive: boolean = true;
  @Input() hasActions: boolean = false;
  @Input() autoFocus: boolean = false;
  @Input() selectTextOnFocus: boolean = false;
  @Input() dropSpecialCharacters: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() fontSize: number = 14;
  @Input() leftIcon: string = '';
  @Input() rightIcon: string = '';
  @Input() rightIconSecond: string = '';
  @Input() colorValue: string = '';
  @Input() isSetWidthPerChar: boolean = false;
  @Input() minCharWidth: number = 20;
  @Input() colorCircle: boolean = false;
  @Input() cancelIcon: string = 'fal fa-times';
  @Input() isCancelIcon: boolean = false;
  @Input() saveIcon: string = 'fal fa-check';
  @Input() mask: string = '';
  @Input() isPasswordHidden: boolean = true;
  @Input() hasShowPasswordIcon: boolean = false;
  @Input() showElipses: boolean = false;
  @Input() imgIcon = null;
  @Input() imgIconClass: string = '';
  @Input() imgIconSecond = null;
  @Input() allowDecimal: boolean = false;
  @Input() allowNegative: boolean = false;
  @Input() hasShowButton: boolean = false;
  @Input() buttonLabel: string = '';
  @Input() tooltipIconClass: string = '';
  @Input() tooltipIconPosition: string = '';
  @Input() tooltipIconText: string = '';
  @Input() tooltipStyleClass: string = '';

  @Output() appFocus = new EventEmitter<any>();
  @Output() appBlur = new EventEmitter<any>();
  @Output() appChange = new EventEmitter<any>();
  @Output() appOnInputChange = new EventEmitter<any>();
  @Output() appClickOutside = new EventEmitter<any>();
  @Output() appCancel = new EventEmitter<any>();
  @Output() appSave = new EventEmitter<any>();
  @Output() appKeyUp = new EventEmitter<any>();
  @Output() appKeyPressEvent = new EventEmitter<any>();
  @Output() appEnter = new EventEmitter<any>();
  @Output() appTabPress = new EventEmitter<any>();
  @Output() appClickInput = new EventEmitter<any>();
  @Output() keyPress = new EventEmitter<any>();
  @Output() iconClicked = new EventEmitter<any>();
  @Output() secondIconClicked = new EventEmitter<any>();
  @Output() handleButton = new EventEmitter<any>();
  @Output() onTooltipIconClicked = new EventEmitter<any>();

  @ViewChild('inputControl', { static: true }) inputControl: ElementRef;
  @ViewChild('inputContainer', { static: true }) inputContainer: ElementRef;

  setActiveTimeOut: any;
  isActive: boolean = false;
  clickOutsideListener: any;
  text: string = '';
  timeOut: any = '';

  constructor(private helperService: HelperService) {}

  ngOnInit() {
    this.setStartUpValue();
  }

  ngOnChanges(change: any) {
    const { autoFocus } = change;
    if (autoFocus) {
      if (!autoFocus.previousValue && autoFocus.currentValue) {
        if (this.inputControl.nativeElement) {
          this.setActiveTimeOut = setTimeout(() => {
            this.inputControl.nativeElement.focus();
          }, 5);
        }
      }
    }
  }

  ngAfterContentInit() {
    if (this.autoFocus) {
      this.inputControl.nativeElement.focus();
    }
  }

  setStartUpValue() {
    if (this.thousandSeparator === ',') {
      this.mask = 'separator.2';
    }

    if (this.isSetWidthPerChar && this.value && this.value.length > this.minCharWidth) {
      this.inputControl.nativeElement.style.width = `${this.value.length}ch`;
    }
  }

  addOutsideClickListener() {
    this.clickOutsideListener = this.helperService.documentClickedTarget.subscribe((target) => {
      if (this.inputContainer.nativeElement.contains(target)) {
      } else {
        this.isActive = false;
        this.appClickOutside.emit(this.value);
        this.removeOutsideClickListener();
      }
    });

    if (this.isSetWidthPerChar) {
      this.inputControl.nativeElement.style.width = null;
    }
  }

  removeOutsideClickListener() {
    if (this.clickOutsideListener) {
      this.clickOutsideListener.unsubscribe();
    }

    if (this.isSetWidthPerChar && this.value && this.value.length > this.minCharWidth) {
      this.inputControl.nativeElement.style.width = `${this.value.length}ch`;
    }
  }

  onFocus() {
    if (this.disabled) {
      return;
    }
    this.isActive = true;
    this.inputControl.nativeElement.focus();

    if (this.selectTextOnFocus) {
      this.timeOut = setTimeout(() => {
        this.inputControl.nativeElement.select();
      }, 10);
    }

    this.appFocus.emit();
    this.addOutsideClickListener();
  }

  onClick() {
    this.appClickInput.emit();
  }

  onKeyPress(value) {
    const eventObj = {
      event: value,
      value: this.value,
      inputControl: this.inputControl,
    };
    this.keyPress.emit(this.value);
    this.appKeyPressEvent.emit(eventObj);
    if (this.dropSpecialCharacters) {
      return new RegExp('^[a-zA-Z0-9]*$').test(value.key);
    }

    if (this.allowDecimal) {
      let obj: AllowDecimalMask = {
        event: value,
        ngModelValue: this.value,
        inputControl: this.inputControl,
        isChanged: false,
      };
      let isAllowChange: boolean = this.helperService.onKeyPress(obj, this.allowNegative);
      //if (obj.isChanged) {
      //  this.value = obj.ngModelValue;
      //}
      return isAllowChange;
    }
  }

  validatePaste(event) {
    if (this.allowDecimal) {
      let obj: AllowDecimalMask = {
        event: event,
        ngModelValue: this.value,
        inputControl: this.inputControl,
        isChanged: false,
      };
      let isAllowChange: boolean = this.helperService.validatePaste(obj);
      //if (obj.isChanged) {
      //  this.value = obj.ngModelValue;
      //}
      return isAllowChange;
    }
  }

  onBlur() {
    this.isActive = false;
    this.appBlur.emit(this.value);
    this.removeOutsideClickListener();

    if (this.setActiveTimeOut) {
      clearInterval(this.setActiveTimeOut);
    }

    if (this.timeOut) {
      clearInterval(this.timeOut);
    }
  }

  onModalChange(value: string) {
    this.appChange.emit(value);
  }

  onInputChange(event) {
    this.appOnInputChange.emit(event.target && event.target.value);
  }

  onCancel() {
    this.appCancel.emit(this.value);
  }

  onSave() {
    this.appSave.emit(this.value);
  }

  onKeyUp(event) {
    this.appKeyUp.emit(this.value);
  }

  onEnter() {
    this.appEnter.emit(this.value);
  }

  onTabPress() {
    this.appTabPress.emit();
  }

  toggleHidePassword() {
    this.isPasswordHidden = !this.isPasswordHidden;
    this.type = this.isPasswordHidden ? 'password' : 'text';
  }

  handleInputButton() {
    this.handleButton.emit();
  }
}
